import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';

import useAutomationsStore from '../../../store/useAutomationsStore';
import { useStoreTheme } from '../../../store/storeTheme';
import useAPI from '../../../hooks/useAPI';
import useCompany from '../../../hooks/useCompany';
import { nodeTypes } from './components/nodes/nodeRegistry';
import NodePanel from './components/NodePanel';
import NodeConfigDrawer from './components/NodeConfigDrawer';
import EditorTopBar from './components/EditorTopBar';
import useWorkflowEditor from './hooks/useWorkflowEditor';
import useNodeDrag from './hooks/useNodeDrag';
import WorkflowStarter from './components/WorkflowStarter';
import ExecutionsPanel from './components/ExecutionsPanel';
import AIChatPanel from './components/AIChatPanel';

function EditorCanvas() {
  const { id } = useParams();
  const navigate = useNavigate();
  const reactFlowWrapper = useRef(null);
  const api = useAPI();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id;

  const {
    currentWorkflow,
    isLoading,
    loadWorkflow,
    saveCurrentWorkflow,
    toggleWorkflowStatus,
    clearEditor,
    setNodes,
    setEdges,
  } = useAutomationsStore();

  const [starterDismissed, setStarterDismissed] = useState(false);
  const [showExecutions, setShowExecutions] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);

  const { nodes, edges, onNodesChange, onEdgesChange, onConnect, onReconnect, onEdgeDoubleClick } =
    useWorkflowEditor();
  const { onDragOver, onDrop } = useNodeDrag();
  const setSidebarCollapsed = useStoreTheme((s) => s.setSidebarCollapsed);
  const wasCollapsedRef = useRef(useStoreTheme.getState().sidebarCollapsed);

  const prevCompanyRef = useRef(companyId);
  useEffect(() => {
    if (!companyId) return;
    // Company changed while editing → redirect to workflow list
    if (prevCompanyRef.current && prevCompanyRef.current !== companyId) {
      prevCompanyRef.current = companyId;
      navigate('/dashboard/workflows');
      return;
    }
    prevCompanyRef.current = companyId;
    wasCollapsedRef.current = useStoreTheme.getState().sidebarCollapsed;
    setSidebarCollapsed(true);
    loadWorkflow(api, companyId, id);
    return () => {
      clearEditor();
      setSidebarCollapsed(wasCollapsedRef.current);
    };
  }, [id, companyId]);

  const { setTokensAndNetworks } = useAutomationsStore();
  useEffect(() => {
    if (!companyId) return;
    api.getSmartContractsByCompany(companyId).then(setTokensAndNetworks).catch(() => {});
  }, [companyId]);

  const handleSave = async () => {
    try {
      await saveCurrentWorkflow(api, companyId);
      toast.success('Workflow saved');
    } catch {
      toast.error('Failed to save workflow');
    }
  };

  const handleToggleStatus = async () => {
    if (!currentWorkflow) return;
    try {
      await toggleWorkflowStatus(api, companyId, currentWorkflow.id);
      toast.success(
        currentWorkflow.active ? 'Workflow deactivated' : 'Workflow activated'
      );
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleLoadVersion = async (versionId) => {
    try {
      await loadWorkflow(api, companyId, id, versionId);
      toast.success(versionId ? 'Version loaded' : 'Latest version loaded');
    } catch {
      toast.error('Failed to load version');
    }
  };

  const handleRestoreLatest = async () => {
    try {
      await loadWorkflow(api, companyId, id);
      toast.success('Restored to latest version');
    } catch {
      toast.error('Failed to restore');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (!currentWorkflow) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-400">Workflow not found</p>
      </div>
    );
  }

  const isNew = !starterDismissed && nodes.length === 0 && !currentWorkflow.file;

  const handleStarterSelect = (selection) => {
    if (selection.type === 'template') {
      setNodes(selection.nodes);
      setEdges(selection.edges);
    }
    setStarterDismissed(true);
  };

  if (isNew) {
    return (
      <div className="flex flex-col h-full -mx-6 -my-4 border-t border-gray-200">
        <EditorTopBar
          workflow={currentWorkflow}
          onBack={() => navigate('/dashboard/workflows')}
          onSave={handleSave}
          onToggleStatus={handleToggleStatus}
          onLoadVersion={handleLoadVersion}
          onRestoreLatest={handleRestoreLatest}
          showExecutions={showExecutions}
          onToggleExecutions={() => setShowExecutions(!showExecutions)}
          showAIChat={showAIChat}
          onToggleAIChat={() => setShowAIChat(!showAIChat)}
        />
        <WorkflowStarter onSelect={handleStarterSelect} api={api} companyId={companyId} workflowId={currentWorkflow.id} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full -mx-6 -my-4 border-t border-gray-200">
      <EditorTopBar
        workflow={currentWorkflow}
        onBack={() => navigate('/dashboard/workflows')}
        onSave={handleSave}
        onToggleStatus={handleToggleStatus}
        onLoadVersion={handleLoadVersion}
        onRestoreLatest={handleRestoreLatest}
        showExecutions={showExecutions}
        onToggleExecutions={() => setShowExecutions(!showExecutions)}
        showAIChat={showAIChat}
        onToggleAIChat={() => setShowAIChat(!showAIChat)}
      />

      <div className="flex flex-1 overflow-hidden" ref={reactFlowWrapper}>
        <NodePanel />

        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onReconnect={onReconnect}
            onEdgeDoubleClick={onEdgeDoubleClick}
            onDragOver={onDragOver}
            onDrop={onDrop}
            nodeTypes={nodeTypes}
            fitView
            snapToGrid
            snapGrid={[16, 16]}
            edgesReconnectable
            deleteKeyCode={['Backspace', 'Delete']}
            defaultEdgeOptions={{
              animated: true,
              interactionWidth: 20,
              style: { stroke: '#94a3b8', strokeWidth: 2 },
            }}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#e2e8f0" gap={16} size={1} />
            <Controls
              position="bottom-right"
              showInteractive={false}
              className="!bg-white !border-gray-200 !shadow-md !rounded-xl"
            />
            <MiniMap
              position="bottom-left"
              nodeColor={(node) => {
                if (node.type === 'triggerNode') return '#2461DD';
                if (node.type === 'actionNode') return '#059669';
                if (node.type === 'walletNode') return '#7C3AED';
                if (node.type === 'wallettwoNode') return '#0891B2';
                if (node.type === 'conditionNode') return '#D97706';
                return '#94a3b8';
              }}
              className="!bg-gray-50 !border-gray-200 !rounded-xl"
              maskColor="rgba(0,0,0,0.08)"
            />
          </ReactFlow>
        </div>

        <NodeConfigDrawer />
        {showAIChat && (
          <AIChatPanel
            api={api}
            companyId={companyId}
            workflowId={currentWorkflow.id}
            onClose={() => setShowAIChat(false)}
          />
        )}
        {showExecutions && (
          <ExecutionsPanel
            api={api}
            companyId={companyId}
            workflowId={currentWorkflow.id}
            onClose={() => setShowExecutions(false)}
          />
        )}
      </div>
    </div>
  );
}

export default function WorkflowEditor() {
  return (
    <ReactFlowProvider>
      <EditorCanvas />
    </ReactFlowProvider>
  );
}
